import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "motion/react";
import RabbitProgress from "../components/RabbitProgress";

// ── Types ──────────────────────────────────────────────────────────────────────

interface CalcResult {
  success: boolean;
  error?: string;
  holding_days?: number;
  holding_years?: number;
  is_long_term?: boolean;
  gain_type?: string;
  section_50c_applies?: boolean;
  effective_sale_value?: number;
  net_sale_consideration?: number;
  cii_purchase?: number;
  cii_sale?: number;
  purchase_fy?: number;
  sale_fy?: number;
  cii_purchase_fy?: number;
  ltcg_with_indexation?: number;
  ltcg_without_indexation?: number;
  tax_with_indexation?: number;
  tax_without_indexation?: number;
  recommended_option?: string;
  chosen_option?: string;
  ltcg?: number;
  stcg?: number;
  tax_rate?: number | string;
  post_budget_2024?: boolean;
  purchase_before_budget_2024?: boolean;
  both_options_available?: boolean;
  exemption_details?: ExemptionDetail[];
  total_exemption?: number;
  taxable_ltcg?: number;
  tax_on_gain?: number;
  cess?: number;
  total_tax_payable?: number;
  warnings?: string[];
}

interface ExemptionDetail {
  section_new: string;
  section_old: string;
  description: string;
  exemption_amount: number;
  investment_made: number;
  notes: string;
  time_limits: string;
}

// ── Legal text for info modals ─────────────────────────────────────────────────

const LEGAL_TEXT: Record<string, { title: string; old: string; text: string; conditions: string[] }> = {
  "82": {
    title: "Profit on sale of property used for residence",
    old: "Section 54, IT Act 1961",
    text: "Where an individual or Hindu undivided family has long-term capital gains arising from the transfer of a capital asset, being buildings or lands appurtenant thereto, and being a residential house, the income of which is chargeable under the head 'Income from house property' (original asset); and has within one year before or two years after the date of such transfer purchased, or has within three years after that date constructed, one residential house in India (new asset)...",
    conditions: [
      "Available to Individual and HUF only",
      "Sold property must be a residential house (Income from House Property)",
      "Long Term Capital Gain only",
      "New house must be in India",
      "Purchase: 1 year before or 2 years after date of sale",
      "Construction: within 3 years after date of sale",
      "Cap: Cost of new house capped at Rs 10 crore (Sec 82(7))",
      "Option to buy 2 houses if LTCG does not exceed Rs 2 crore (Sec 82(5))",
      "Unutilised amount to be deposited in CGAS before ITR due date",
      "If CGAS not utilised within prescribed period, taxed as LTCG",
    ],
  },
  "83": {
    title: "Capital gains on transfer of land used for agricultural purposes",
    old: "Section 54B, IT Act 1961",
    text: "Where an assessee, being an individual or a Hindu undivided family, has capital gains arising from the transfer of a capital asset, being land, which was used by the assessee or his parent, or the Hindu undivided family for agricultural purposes (original asset), in two years immediately preceding the date of transfer; and has, within two years after that date, purchased any other land for being used for agricultural purposes (new asset)...",
    conditions: [
      "Available to Individual and HUF only",
      "Sold asset must be agricultural land (urban or rural)",
      "Land must be used for agriculture by assessee, parent, or HUF member for 2 years before sale",
      "LTCG only (also applies to STCG on agricultural land — check with CA)",
      "New agricultural land must be purchased within 2 years of sale",
      "New land must be used for agricultural purposes",
      "Unutilised amount to be deposited in CGAS before ITR due date",
      "If CGAS not utilised within 2 years, taxed as CG",
    ],
  },
  "84": {
    title: "Capital gains on compulsory acquisition of lands and buildings",
    old: "Section 54D, IT Act 1961",
    text: "Where an assessee has capital gains arising from the transfer by way of compulsory acquisition under any law, of a capital asset being land or building or any right in land or building, forming part of an industrial undertaking belonging to him, which was being used by the assessee for the business of the said undertaking in the two years immediately preceding the date of transfer (original asset); and within three years after that date, purchased any other land or building or constructed any other building for shifting or re-establishing the said undertaking or setting up another industrial undertaking (new asset)...",
    conditions: [
      "Available to ALL assessees (Individual, HUF, Company, Firm, etc.)",
      "Transfer must be by COMPULSORY ACQUISITION under any law",
      "Property must be land/building forming part of an INDUSTRIAL undertaking",
      "Industrial undertaking must have been used for business for 2 years before acquisition",
      "New industrial property must be purchased/constructed within 3 years",
      "CGAS deposit allowed if investment not made before ITR due date",
    ],
  },
  "85": {
    title: "Capital gains not charged on investment in certain bonds",
    old: "Section 54EC, IT Act 1961",
    text: "Where an assessee has long-term capital gains arising from the transfer of land or building, or both, (original asset); and within six months after the date of such transfer, invested whole or part of the capital gains in a long-term specified asset (new asset)...",
    conditions: [
      "Available to ALL assessees",
      "LTCG only, and only on transfer of LAND or BUILDING (or both)",
      "Investment must be made within 6 months of date of sale",
      "Maximum Rs 50,00,000 per financial year (Sec 85(2))",
      "Applies across all transfers — total FY limit is Rs 50 lakhs",
      "Bond lock-in: 5 years (redeem after 5 years)",
      "If bond transferred/converted within 5 years, LTCG becomes taxable",
      "Loan against bond treated as conversion — forfeits exemption",
      "No deduction under Section 123 (80C equivalent) for bond investment",
      "Eligible bonds: NHAI or REC bonds issued on/after 1st April 2018",
    ],
  },
  "86": {
    title: "Capital gains on transfer of certain capital assets — investment in residential house",
    old: "Section 54F, IT Act 1961",
    text: "If an individual or a Hindu undivided family has capital gains arising from the transfer of any long-term capital asset, not being a residential house (original asset); and within one year before, or two years after, the date of such transfer, purchased, or has within three years after that date constructed, one residential house in India (new asset)...",
    conditions: [
      "Available to Individual and HUF only",
      "Sold asset must NOT be a residential house (any other LTCG asset)",
      "PROPORTIONATE exemption = LTCG × New House Cost ÷ Net Sale Consideration",
      "Full exemption if new house cost ≥ net consideration",
      "Must NOT own more than 1 residential house on date of sale (excluding new house)",
      "Must NOT purchase another house (other than new) within 1 year of sale",
      "Must NOT construct another house within 3 years of sale",
      "New house must be in India",
      "Cost cap: Rs 10 crore for new house (Sec 86(8))",
      "Net consideration cap: Rs 10 crore for CGAS (Sec 86(9))",
      "CGAS based on NET CONSIDERATION (not capital gain)",
      "Purchase: 1 year before or 2 years after; Construction: 3 years after",
    ],
  },
};

// ── Helpers ────────────────────────────────────────────────────────────────────

const fmtInr = (v?: number | null) => {
  if (v == null || isNaN(v)) return "—";
  return `₹ ${Math.abs(v).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
};

const fmtInrSigned = (v?: number | null) => {
  if (v == null || isNaN(v)) return "—";
  const abs = Math.abs(v).toLocaleString("en-IN", { maximumFractionDigits: 0 });
  return v < 0 ? `(₹ ${abs})` : `₹ ${abs}`;
};

function HoldingBadge({ days }: { days: number }) {
  const isLT = days >= 730;
  const y = Math.floor(days / 365);
  const m = Math.floor((days % 365) / 30);
  const label = `${y > 0 ? `${y}y ` : ""}${m > 0 ? `${m}m ` : ""}${days % 30}d`;
  return (
    <span style={{
      display: "inline-block",
      marginLeft: 8,
      padding: "2px 10px",
      borderRadius: 20,
      fontSize: 12,
      fontWeight: 700,
      background: isLT ? "#E2EFDA" : "#FFF2CC",
      color: isLT ? "#375623" : "#7F6000",
      border: `1px solid ${isLT ? "#A9C9A0" : "#C9A84C"}`,
    }}>
      {label} — {isLT ? "Long Term" : "Short Term"}
    </span>
  );
}

function InfoModal({ section, onClose }: { section: string; onClose: () => void }) {
  const info = LEGAL_TEXT[section];
  if (!info) return null;
  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.5)",
      display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
    }} onClick={onClose}>
      <div style={{
        background: "var(--card-bg, #fff)", borderRadius: 12, maxWidth: 640, width: "100%",
        maxHeight: "80vh", overflow: "auto", padding: 28, boxShadow: "0 8px 40px rgba(0,0,0,0.2)",
      }} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 13, color: "#2E75B6", fontWeight: 700, marginBottom: 4 }}>
              Section {section} — IT Act 2025
            </div>
            <div style={{ fontSize: 11, color: "#888", marginBottom: 8 }}>Equivalent: {info.old}</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "var(--text-primary, #111)" }}>{info.title}</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#888", padding: "0 4px" }}>✕</button>
        </div>
        <div style={{ fontSize: 12, color: "#555", lineHeight: 1.6, marginBottom: 16, padding: "12px 16px", background: "#f8f9fa", borderRadius: 8, borderLeft: "3px solid #2E75B6" }}>
          <em>{info.text}</em>
        </div>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8, color: "var(--text-primary, #111)" }}>Key Conditions:</div>
        <ul style={{ margin: 0, paddingLeft: 20 }}>
          {info.conditions.map((c, i) => (
            <li key={i} style={{ fontSize: 12, color: "var(--text-primary, #222)", marginBottom: 6, lineHeight: 1.5 }}>{c}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// ── DateInput / TaxYearInput / SurchargeTable ─────────────────────────────────

function DateInput({ style, value, onChange, placeholder }: {
  style: React.CSSProperties; value: string; onChange: (v: string) => void; placeholder?: string;
}) {
  const handle = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/[^0-9]/g, "").slice(0, 8);
    let out = raw;
    if (raw.length >= 3) out = raw.slice(0, 2) + "/" + raw.slice(2);
    if (raw.length >= 5) out = raw.slice(0, 2) + "/" + raw.slice(2, 4) + "/" + raw.slice(4);
    onChange(out);
  };
  return <input style={style} value={value} onChange={handle} placeholder={placeholder || "DD/MM/YYYY"} maxLength={10} />;
}

function TaxYearInput({ style, value, onChange }: {
  style: React.CSSProperties; value: string; onChange: (v: string) => void;
}) {
  const handle = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/[^0-9]/g, "").slice(0, 6);
    if (digits.length <= 4) {
      onChange(digits);
    } else {
      onChange(digits.slice(0, 4) + "-" + digits.slice(4, 6));
    }
  };
  return <input style={style} value={value} onChange={handle} placeholder="e.g. 2025-26" maxLength={7} />;
}

function SurchargeTable() {
  const rows: [string, string, string, string][] = [
    ["≤ ₹50 Lakhs",      "Nil",   "20.80%", "13.00%"],
    ["₹50L – ₹1 Crore",  "10%",   "22.88%", "14.30%"],
    ["₹1 Cr – ₹2 Crore", "15%",   "23.92%", "14.95%"],
    ["₹2 Cr – ₹5 Crore", "25%",   "26.00%", "16.25%"],
    ["> ₹5 Crore",        "25%†", "26.00%†", "16.25%†"],
  ];
  return (
    <div style={{ borderTop: "1px solid var(--border, #eee)", marginTop: 12, paddingTop: 10 }}>
      <div style={{ fontSize: 11, color: "var(--text-muted, #888)", marginBottom: 6, fontWeight: 600 }}>
        Surcharge on LTCG (by total income)
      </div>
      <table style={{ width: "100%", fontSize: 10, borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ background: "#1F4E79", color: "#fff" }}>
            <th style={{ padding: "4px 6px", textAlign: "left" }}>Income Range</th>
            <th style={{ padding: "4px 6px", textAlign: "center" }}>Surcharge</th>
            <th style={{ padding: "4px 6px", textAlign: "center" }}>@20%</th>
            <th style={{ padding: "4px 6px", textAlign: "center" }}>@12.5%</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(([inc, sur, t20, t125], i) => (
            <tr key={i} style={{ background: i % 2 === 0 ? "#EBF3FB" : "#fff" }}>
              <td style={{ padding: "3px 6px", color: "var(--text-primary, #333)" }}>{inc}</td>
              <td style={{ padding: "3px 6px", textAlign: "center", fontWeight: 600 }}>{sur}</td>
              <td style={{ padding: "3px 6px", textAlign: "center" }}>{t20}</td>
              <td style={{ padding: "3px 6px", textAlign: "center" }}>{t125}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ fontSize: 10, color: "#888", marginTop: 3 }}>
        † Max 25% surcharge under new regime. Rates incl. 4% cess. Consult CA for exact liability.
      </div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────

export default function PropertyGain() {
  // Form state
  const [clientName, setClientName] = useState("");
  const [financialYear, setFinancialYear] = useState("2025-26");
  const [propertyType, setPropertyType] = useState("residential");
  const [natureOfSale, setNatureOfSale] = useState("outright_sale");
  const [dateOfPurchase, setDateOfPurchase] = useState("");
  const [dateOfSale, setDateOfSale] = useState("");
  const [costOfPurchase, setCostOfPurchase] = useState("");
  const [costOfImprovement, setCostOfImprovement] = useState("");
  const [expensesOnPurchase, setExpensesOnPurchase] = useState("");
  const [saleConsideration, setSaleConsideration] = useState("");
  const [stampDutyValue, setStampDutyValue] = useState("");
  const [expensesOnSale, setExpensesOnSale] = useState("");
  const [sellerStatus, setSellerStatus] = useState("individual_resident");
  const [numCoowners, setNumCoowners] = useState("1");
  const [ownershipShare, setOwnershipShare] = useState("100");
  const [modeOfAcquisition, setModeOfAcquisition] = useState("purchase");
  const [prevOwnerDate, setPrevOwnerDate] = useState("");
  const [prevOwnerCost, setPrevOwnerCost] = useState("");
  const [taxOption, setTaxOption] = useState("auto");

  // Section 82
  const [s82claim, setS82claim] = useState(false);
  const [s82type, setS82type] = useState("purchased_after_sale");
  const [s82cost, setS82cost] = useState("");
  const [s82date, setS82date] = useState("");
  const [s82india, setS82india] = useState(true);
  const [s82cgas, setS82cgas] = useState("");
  const [s82cgasBank, setS82cgasBank] = useState("");

  // Section 83
  const [s83claim, setS83claim] = useState(false);
  const [s83usedAg, setS83usedAg] = useState(true);
  const [s83landCost, setS83landCost] = useState("");
  const [s83cgas, setS83cgas] = useState("");

  // Section 84
  const [s84claim, setS84claim] = useState(false);
  const [s84industrial, setS84industrial] = useState(true);
  const [s84investment, setS84investment] = useState("");

  // Section 85
  const [s85claim, setS85claim] = useState(false);
  const [s85bond, setS85bond] = useState("NHAI");
  const [s85amount, setS85amount] = useState("");
  const [s85date, setS85date] = useState("");

  // Section 86
  const [s86claim, setS86claim] = useState(false);
  const [s86houses, setS86houses] = useState("0");
  const [s86cost, setS86cost] = useState("");
  const [s86date, setS86date] = useState("");
  const [s86india, setS86india] = useState(true);
  const [s86cgas, setS86cgas] = useState("");

  // Inherited / FMV fields
  const [inheritedFmv, setInheritedFmv] = useState("");
  const [sdvAtInheritance, setSdvAtInheritance] = useState("");

  // UI state
  const [result, setResult] = useState<CalcResult | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadingJpeg, setDownloadingJpeg] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [infoModal, setInfoModal] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const resultPanelRef = useRef<HTMLDivElement>(null);

  // Computed holding period
  const holdingDays = (() => {
    try {
      if (!dateOfPurchase || !dateOfSale) return null;
      const [pd, pm, py] = dateOfPurchase.split("/").map(Number);
      const [sd, sm, sy] = dateOfSale.split("/").map(Number);
      const p = new Date(py, pm - 1, pd);
      const s = new Date(sy, sm - 1, sd);
      if (isNaN(p.getTime()) || isNaN(s.getTime())) return null;
      return Math.floor((s.getTime() - p.getTime()) / 86400000);
    } catch { return null; }
  })();
  const isLongTerm = holdingDays !== null && holdingDays >= 730;

  // Tax year vs date-of-sale conflict check
  const dateOfSaleFY = (() => {
    try {
      const parts = dateOfSale.split("/");
      if (parts.length < 3) return null;
      const m = parseInt(parts[1]), y = parseInt(parts[2]);
      if (!y || !m) return null;
      const fy = m >= 4 ? y : y - 1;
      return `${fy}-${String(fy + 1).slice(2)}`;
    } catch { return null; }
  })();
  const taxYearConflict = dateOfSaleFY && financialYear.length === 7 && dateOfSaleFY !== financialYear;

  // Build request payload
  const buildPayload = useCallback(() => ({
    property_type: propertyType,
    nature_of_sale: natureOfSale,
    date_of_purchase: dateOfPurchase,
    date_of_sale: dateOfSale,
    cost_of_purchase: parseFloat(costOfPurchase) || 0,
    cost_of_improvement: parseFloat(costOfImprovement) || 0,
    expenses_on_purchase: parseFloat(expensesOnPurchase) || 0,
    sale_consideration: parseFloat(saleConsideration) || 0,
    stamp_duty_value: parseFloat(stampDutyValue) || 0,
    expenses_on_sale: parseFloat(expensesOnSale) || 0,
    seller_status: sellerStatus,
    number_of_coowners: parseInt(numCoowners) || 1,
    ownership_share_percent: parseFloat(ownershipShare) || 100,
    mode_of_acquisition: modeOfAcquisition,
    previous_owner_purchase_date: prevOwnerDate || null,
    previous_owner_cost: parseFloat(prevOwnerCost) || 0,
    tax_option: taxOption,
    section_82: {
      claiming: s82claim,
      purchase_type: s82type,
      new_house_cost: parseFloat(s82cost) || 0,
      new_house_purchase_date: s82date || null,
      new_house_in_india: s82india,
      cgas_amount: parseFloat(s82cgas) || 0,
      cgas_bank: s82cgasBank,
    },
    section_83: {
      claiming: s83claim,
      used_for_agriculture: s83usedAg,
      new_land_cost: parseFloat(s83landCost) || 0,
      cgas_amount: parseFloat(s83cgas) || 0,
    },
    section_84: {
      claiming: s84claim,
      used_for_industrial: s84industrial,
      new_property_investment: parseFloat(s84investment) || 0,
    },
    section_85: {
      claiming: s85claim,
      bond_name: s85bond,
      amount_invested: parseFloat(s85amount) || 0,
      investment_date: s85date || null,
    },
    section_86: {
      claiming: s86claim,
      houses_owned_on_sale_date: parseInt(s86houses) || 0,
      new_house_cost: parseFloat(s86cost) || 0,
      new_house_purchase_date: s86date || null,
      new_house_in_india: s86india,
      cgas_amount: parseFloat(s86cgas) || 0,
    },
    client_name: clientName,
    financial_year: financialYear,
    fmv_april_2001: parseFloat(inheritedFmv) || 0,
    sdv_at_inheritance: parseFloat(sdvAtInheritance) || 0,
  }), [
    propertyType, natureOfSale, dateOfPurchase, dateOfSale,
    costOfPurchase, costOfImprovement, expensesOnPurchase,
    saleConsideration, stampDutyValue, expensesOnSale,
    sellerStatus, numCoowners, ownershipShare, modeOfAcquisition,
    prevOwnerDate, prevOwnerCost, taxOption,
    s82claim, s82type, s82cost, s82date, s82india, s82cgas, s82cgasBank,
    s83claim, s83usedAg, s83landCost, s83cgas,
    s84claim, s84industrial, s84investment,
    s85claim, s85bond, s85amount, s85date,
    s86claim, s86houses, s86cost, s86date, s86india, s86cgas,
    clientName, financialYear, inheritedFmv, sdvAtInheritance,
  ]);

  // Debounced API call
  useEffect(() => {
    if (!dateOfPurchase || !dateOfSale || !saleConsideration || !costOfPurchase) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch("/api/property-gain/calculate", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(buildPayload()),
        });
        const data = await res.json();
        setResult(data);
      } catch {
        // Silently fail on live preview
      }
    }, 500);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [buildPayload, dateOfPurchase, dateOfSale, saleConsideration, costOfPurchase]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch("/api/property-gain/download-report", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(buildPayload()),
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url  = window.URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href = url;
      a.download = `TaxThinker_PropertyGain_${clientName || "Report"}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert("Download failed. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadJpeg = async () => {
    if (!resultPanelRef.current) return;
    setDownloadingJpeg(true);
    try {
      const html2canvas = (await import("html2canvas")).default;
      const canvas = await html2canvas(resultPanelRef.current, {
        backgroundColor: "#ffffff",
        scale: 2,
        onclone: (_doc: Document, el: HTMLElement) => {
          el.style.setProperty("--card-bg", "#ffffff");
          el.style.setProperty("--border", "#e5e7eb");
          el.style.setProperty("--text-primary", "#111111");
          el.style.setProperty("--text-muted", "#888888");
        },
      });
      const link = document.createElement("a");
      link.download = `TaxThinker_PropertyGain_${clientName || "Summary"}.jpg`;
      link.href = canvas.toDataURL("image/jpeg", 0.95);
      link.click();
    } catch {
      alert("JPEG export failed. Please try again.");
    } finally {
      setDownloadingJpeg(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!resultPanelRef.current) return;
    setDownloadingPdf(true);
    try {
      const html2canvas = (await import("html2canvas")).default;
      const canvas = await html2canvas(resultPanelRef.current, {
        backgroundColor: "#ffffff",
        scale: 2,
        onclone: (_doc: Document, el: HTMLElement) => {
          el.style.setProperty("--card-bg", "#ffffff");
          el.style.setProperty("--border", "#e5e7eb");
          el.style.setProperty("--text-primary", "#111111");
          el.style.setProperty("--text-muted", "#888888");
        },
      });
      const imgData = canvas.toDataURL("image/png");
      const win = window.open("", "_blank");
      if (win) {
        win.document.write(
          `<html><head><title>TaxThinker — ${clientName || "Property Gain"}</title>` +
          `<style>body{margin:0;padding:20px;background:#fff;}img{max-width:100%;height:auto;display:block;}</style>` +
          `</head><body><img src="${imgData}"/></body></html>`
        );
        win.document.close();
        setTimeout(() => win.print(), 400);
      }
    } catch {
      alert("PDF export failed. Please try again.");
    } finally {
      setDownloadingPdf(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: "100%", padding: "8px 12px", borderRadius: 8,
    border: "1.5px solid var(--border, #ddd)", background: "var(--card-bg, #fff)",
    color: "var(--text-primary, #111)", fontSize: 14, outline: "none",
    boxSizing: "border-box",
  };
  const labelStyle: React.CSSProperties = { fontSize: 13, fontWeight: 600, color: "var(--text-primary, #333)", marginBottom: 4, display: "block" };
  const sectionHeadStyle: React.CSSProperties = {
    fontSize: 14, fontWeight: 700, color: "#fff",
    background: "#1F4E79", padding: "10px 16px", borderRadius: 8,
    marginBottom: 16, marginTop: 4,
  };
  const cardStyle: React.CSSProperties = {
    background: "var(--card-bg, #fff)", borderRadius: 12,
    border: "1.5px solid var(--border, #e5e7eb)", padding: "20px 24px",
    marginBottom: 16,
  };

  const TABS = ["Property Details", "Ownership", "Tax Option", "Exemptions", "Review & Download"];

  // Show/hide tabs based on LTCG
  const visibleTabs = isLongTerm
    ? TABS
    : TABS.filter((_, i) => i !== 2 && i !== 3);

  return (
    <div style={{ maxWidth: 1280, margin: "0 auto", padding: "24px 16px" }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
          <span style={{ fontSize: 32 }}>🏠</span>
          <div>
            <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: "var(--text-primary, #111)" }}>
              Property Capital Gain Calculator
            </h1>
            <p style={{ margin: 0, fontSize: 13, color: "var(--text-muted, #888)" }}>
              IT Act 2025 (Sections 82–86) | Also references IT Act 1961 (Sections 54–54F)
            </p>
          </div>
        </div>
      </div>

      <div style={{ display: "flex", gap: 20, alignItems: "flex-start" }}>
        {/* ── LEFT: Form (60%) ────────────────────────────────────────────── */}
        <div style={{ flex: "0 0 60%", minWidth: 0 }}>

          {/* Client info row */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
            <div>
              <label style={labelStyle}>Client Name <span style={{ fontWeight: 400, color: "#888" }}>(optional)</span></label>
              <input style={inputStyle} value={clientName} onChange={e => setClientName(e.target.value)} placeholder="e.g. Ramesh Kumar" />
            </div>
            <div>
              <label style={labelStyle}>Financial Year</label>
              <TaxYearInput style={inputStyle} value={financialYear} onChange={setFinancialYear} />
            </div>
          </div>

          {/* Tax year / date-of-sale conflict warning */}
          {taxYearConflict && (
            <div style={{ background: "#FFF2CC", border: "1px solid #C9A84C", borderRadius: 8, padding: "8px 14px", marginBottom: 12, fontSize: 12, color: "#5C3D00" }}>
              <strong>⚠ Tax Year Mismatch:</strong> Date of Sale falls in FY {dateOfSaleFY}, but Financial Year is set to {financialYear}. Please verify.
            </div>
          )}

          {/* Tabs */}
          <div style={{ display: "flex", gap: 4, marginBottom: 16, flexWrap: "wrap" }}>
            {visibleTabs.map((tab, i) => (
              <button key={tab} onClick={() => setActiveTab(i)} style={{
                padding: "7px 14px", borderRadius: 20, fontSize: 12, fontWeight: 600,
                border: "1.5px solid",
                borderColor: activeTab === i ? "#1F4E79" : "var(--border, #ddd)",
                background: activeTab === i ? "#1F4E79" : "transparent",
                color: activeTab === i ? "#fff" : "var(--text-muted, #666)",
                cursor: "pointer",
              }}>
                {i + 1}. {tab}
              </button>
            ))}
          </div>

          {/* Short term warning */}
          {holdingDays !== null && !isLongTerm && (
            <div style={{ background: "#FFF2CC", border: "1px solid #C9A84C", borderRadius: 8, padding: "10px 16px", marginBottom: 16, fontSize: 13, color: "#5C3D00" }}>
              <strong>⚠ Short Term Capital Gain</strong> — Held {holdingDays} days (less than 2 years).
              STCG is taxed at your applicable slab rate. Exemptions under Sections 82–86 (54–54F) are <strong>NOT available</strong>.
            </div>
          )}

          {/* ── TAB 0: Property Details ──────────────────────────────────── */}
          {activeTab === 0 && (
            <div style={cardStyle}>
              <div style={sectionHeadStyle}>Property Details</div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                <div>
                  <label style={labelStyle}>Property Type</label>
                  <select style={inputStyle} value={propertyType} onChange={e => setPropertyType(e.target.value)}>
                    <option value="residential">Residential House Property</option>
                    <option value="commercial">Commercial Property</option>
                    <option value="plot_land">Plot / Land (Non-Agricultural)</option>
                    <option value="industrial">Industrial Property</option>
                    <option value="agricultural_rural">Agricultural Land (Rural)</option>
                    <option value="agricultural_urban">Agricultural Land (Urban)</option>
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Nature of Sale</label>
                  <select style={inputStyle} value={natureOfSale} onChange={e => setNatureOfSale(e.target.value)}>
                    <option value="outright_sale">Outright Sale</option>
                    <option value="compulsory_acquisition">Compulsory Acquisition by Government</option>
                    <option value="exchange">Exchange / Barter</option>
                    <option value="part_sale">Part Sale</option>
                  </select>
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                <div>
                  <label style={labelStyle}>Date of Purchase</label>
                  <DateInput style={inputStyle} value={dateOfPurchase} onChange={setDateOfPurchase} />
                </div>
                <div>
                  <label style={labelStyle}>Date of Sale</label>
                  <DateInput style={inputStyle} value={dateOfSale} onChange={setDateOfSale} />
                </div>
              </div>

              {holdingDays !== null && (
                <div style={{ marginBottom: 12, fontSize: 13, color: "var(--text-muted, #666)" }}>
                  Holding Period: <HoldingBadge days={holdingDays} />
                </div>
              )}

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
                <div>
                  <label style={labelStyle}>Cost of Purchase (₹)</label>
                  <input style={inputStyle} type="number" value={costOfPurchase} onChange={e => setCostOfPurchase(e.target.value)} placeholder="0" />
                </div>
                <div>
                  <label style={labelStyle}>Cost of Improvement (₹)</label>
                  <input style={inputStyle} type="number" value={costOfImprovement} onChange={e => setCostOfImprovement(e.target.value)} placeholder="0" />
                </div>
                <div>
                  <label style={labelStyle}>
                    Expenses on Purchase (₹)
                    <span style={{ marginLeft: 4, fontSize: 10, fontWeight: 400, color: "#888" }}>Stamp duty, brokerage</span>
                  </label>
                  <input style={inputStyle} type="number" value={expensesOnPurchase} onChange={e => setExpensesOnPurchase(e.target.value)} placeholder="0" />
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
                <div>
                  <label style={labelStyle}>Sale Consideration (₹)</label>
                  <input style={inputStyle} type="number" value={saleConsideration} onChange={e => setSaleConsideration(e.target.value)} placeholder="0" />
                </div>
                <div>
                  <label style={labelStyle}>Stamp Duty Value / Circle Rate (₹)</label>
                  <input style={inputStyle} type="number" value={stampDutyValue} onChange={e => setStampDutyValue(e.target.value)} placeholder="0 (optional)" />
                </div>
                <div>
                  <label style={labelStyle}>
                    Expenses on Sale (₹)
                    <span style={{ marginLeft: 4, fontSize: 10, fontWeight: 400, color: "#888" }}>Brokerage, legal fees</span>
                  </label>
                  <input style={inputStyle} type="number" value={expensesOnSale} onChange={e => setExpensesOnSale(e.target.value)} placeholder="0" />
                </div>
              </div>

              {stampDutyValue && saleConsideration && parseFloat(stampDutyValue) > parseFloat(saleConsideration) * 1.10 && (
                <div style={{ background: "#FFF2CC", border: "1px solid #C9A84C", borderRadius: 8, padding: "8px 14px", marginBottom: 12, fontSize: 12, color: "#5C3D00" }}>
                  ⚠ <strong>Section 50C applies</strong>: Stamp Duty Value exceeds 110% of Sale Consideration.
                  SDV (₹ {parseFloat(stampDutyValue).toLocaleString("en-IN")}) will be treated as full value of consideration.
                </div>
              )}

              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
                <button onClick={() => setActiveTab(1)} style={{ padding: "8px 20px", background: "#1F4E79", color: "#fff", border: "none", borderRadius: 8, fontWeight: 600, cursor: "pointer" }}>
                  Next: Ownership →
                </button>
              </div>
            </div>
          )}

          {/* ── TAB 1: Ownership ─────────────────────────────────────────── */}
          {activeTab === 1 && (
            <div style={cardStyle}>
              <div style={sectionHeadStyle}>Ownership Details</div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                <div>
                  <label style={labelStyle}>Seller Status</label>
                  <select style={inputStyle} value={sellerStatus} onChange={e => setSellerStatus(e.target.value)}>
                    <option value="individual_resident">Individual (Resident Indian)</option>
                    <option value="individual_nri">Individual (NRI)</option>
                    <option value="huf">HUF (Hindu Undivided Family)</option>
                    <option value="company">Company</option>
                    <option value="firm">Partnership Firm</option>
                    <option value="llp">LLP</option>
                    <option value="trust">Trust</option>
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Number of Co-owners</label>
                  <select style={inputStyle} value={numCoowners} onChange={e => setNumCoowners(e.target.value)}>
                    <option value="1">1 (Sole owner)</option>
                    <option value="2">2</option>
                    <option value="3">3</option>
                    <option value="4">4</option>
                  </select>
                </div>
              </div>

              {sellerStatus === "individual_nri" && (
                <div style={{ background: "#EBF3FB", border: "1px solid #2E75B6", borderRadius: 8, padding: "10px 14px", marginBottom: 12, fontSize: 12, color: "#1F4E79" }}>
                  ℹ <strong>NRI Seller:</strong> Buyer must deduct TDS @ 20% (+ surcharge + cess) on sale consideration under Section 195 of IT Act.
                  You may apply for a lower TDS certificate from the Income Tax department.
                </div>
              )}

              {parseInt(numCoowners) > 1 && (
                <div style={{ marginBottom: 12 }}>
                  <label style={labelStyle}>Your Ownership Share (%)</label>
                  <input style={inputStyle} type="number" value={ownershipShare} onChange={e => setOwnershipShare(e.target.value)} placeholder="e.g. 50" min="1" max="100" />
                  <div style={{ fontSize: 11, color: "#888", marginTop: 4 }}>
                    Calculation uses only your proportionate share of cost and sale consideration.
                  </div>
                </div>
              )}

              <div style={{ marginBottom: 12 }}>
                <label style={labelStyle}>Mode of Acquisition</label>
                <select style={inputStyle} value={modeOfAcquisition} onChange={e => setModeOfAcquisition(e.target.value)}>
                  <option value="purchase">Purchase (Buy/Sell)</option>
                  <option value="gift">Gift from relative</option>
                  <option value="inheritance">Inheritance / Will</option>
                  <option value="huf_partition">Partition of HUF</option>
                  <option value="compulsory_acquisition">Compulsory Acquisition by Govt.</option>
                  <option value="other">Any other mode</option>
                </select>
              </div>

              {(modeOfAcquisition === "gift" || modeOfAcquisition === "inheritance") && (
                <div style={{ background: "#EBF3FB", border: "1px solid #2E75B6", borderRadius: 8, padding: "14px 16px", marginBottom: 12 }}>
                  <div style={{ fontSize: 12, color: "#1F4E79", fontWeight: 700, marginBottom: 10 }}>
                    ℹ Previous Owner Details
                    <span style={{ fontWeight: 400, marginLeft: 8 }}>Holding period and cost computed from previous owner's acquisition.</span>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 10 }}>
                    <div>
                      <label style={labelStyle}>Previous Owner's Date of Purchase</label>
                      <DateInput style={inputStyle} value={prevOwnerDate} onChange={setPrevOwnerDate} />
                    </div>
                    <div>
                      <label style={labelStyle}>Previous Owner's Cost (₹)</label>
                      <input style={inputStyle} type="number" value={prevOwnerCost} onChange={e => setPrevOwnerCost(e.target.value)} placeholder="0" />
                    </div>
                  </div>
                  {/* FMV / SDV fields for inherited property */}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <div>
                      <label style={labelStyle}>
                        FMV as on 01/04/2001 (₹)
                        <span style={{ marginLeft: 4, fontSize: 10, fontWeight: 400, color: "#888" }}>If original purchase was before 01-Apr-2001</span>
                      </label>
                      <input style={inputStyle} type="number" value={inheritedFmv} onChange={e => setInheritedFmv(e.target.value)} placeholder="0 (optional)" />
                    </div>
                    <div>
                      <label style={labelStyle}>
                        SDV at time of gift/inheritance (₹)
                        <span style={{ marginLeft: 4, fontSize: 10, fontWeight: 400, color: "#888" }}>Stamp Duty Value when received</span>
                      </label>
                      <input style={inputStyle} type="number" value={sdvAtInheritance} onChange={e => setSdvAtInheritance(e.target.value)} placeholder="0 (optional)" />
                    </div>
                  </div>
                  {inheritedFmv && prevOwnerDate && (() => {
                    try {
                      const [,, y] = prevOwnerDate.split("/").map(Number);
                      if (y && y < 2001) {
                        return (
                          <div style={{ marginTop: 8, fontSize: 11, color: "#1F4E79", background: "#D6E4F0", borderRadius: 6, padding: "6px 10px" }}>
                            FMV of ₹ {parseFloat(inheritedFmv).toLocaleString("en-IN")} will be used as cost basis (property acquired before 01-04-2001).
                          </div>
                        );
                      }
                    } catch { }
                    return null;
                  })()}
                </div>
              )}

              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8 }}>
                <button onClick={() => setActiveTab(0)} style={{ padding: "8px 20px", background: "transparent", color: "#1F4E79", border: "1.5px solid #1F4E79", borderRadius: 8, fontWeight: 600, cursor: "pointer" }}>
                  ← Back
                </button>
                <button onClick={() => setActiveTab(isLongTerm ? 2 : 2)} style={{ padding: "8px 20px", background: "#1F4E79", color: "#fff", border: "none", borderRadius: 8, fontWeight: 600, cursor: "pointer" }}>
                  Next →
                </button>
              </div>
            </div>
          )}

          {/* ── TAB 2: Tax Option (only for LTCG) ────────────────────────── */}
          {activeTab === 2 && isLongTerm && (
            <div style={cardStyle}>
              <div style={sectionHeadStyle}>Tax Option</div>

              {result?.both_options_available ? (
                <>
                  <div style={{ background: "#EBF3FB", border: "1px solid #2E75B6", borderRadius: 10, padding: "16px 20px", marginBottom: 16 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#1F4E79", marginBottom: 12 }}>
                      You have TWO options for tax calculation (Budget 2024)
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                      <div style={{ background: result?.recommended_option === "with_indexation" ? "#E2EFDA" : "#fff", border: `2px solid ${result?.recommended_option === "with_indexation" ? "#375623" : "#ddd"}`, borderRadius: 8, padding: "14px 16px" }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: "#1F4E79", marginBottom: 6 }}>Option A: 20% WITH Indexation</div>
                        <div style={{ fontSize: 12, color: "#555", marginBottom: 4 }}>Indexed Cost: {fmtInr(result?.net_sale_consideration && result?.ltcg_with_indexation != null ? result.net_sale_consideration - result.ltcg_with_indexation : undefined)}</div>
                        <div style={{ fontSize: 12, color: "#555", marginBottom: 4 }}>Capital Gain: {fmtInr(result?.ltcg_with_indexation)}</div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: "#333" }}>Tax @ 20%: {fmtInr(result?.tax_with_indexation)}</div>
                        {result?.recommended_option === "with_indexation" && <div style={{ marginTop: 6, fontSize: 11, color: "#375623", fontWeight: 700 }}>✅ RECOMMENDED</div>}
                      </div>
                      <div style={{ background: result?.recommended_option === "without_indexation" ? "#E2EFDA" : "#fff", border: `2px solid ${result?.recommended_option === "without_indexation" ? "#375623" : "#ddd"}`, borderRadius: 8, padding: "14px 16px" }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: "#1F4E79", marginBottom: 6 }}>Option B: 12.5% WITHOUT Indexation</div>
                        <div style={{ fontSize: 12, color: "#555", marginBottom: 4 }}>Cost (unindexed): {fmtInr(result?.net_sale_consideration && result?.ltcg_without_indexation != null ? result.net_sale_consideration - result.ltcg_without_indexation : undefined)}</div>
                        <div style={{ fontSize: 12, color: "#555", marginBottom: 4 }}>Capital Gain: {fmtInr(result?.ltcg_without_indexation)}</div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: "#333" }}>Tax @ 12.5%: {fmtInr(result?.tax_without_indexation)}</div>
                        {result?.recommended_option === "without_indexation" && <div style={{ marginTop: 6, fontSize: 11, color: "#375623", fontWeight: 700 }}>✅ RECOMMENDED</div>}
                      </div>
                    </div>
                    {result?.tax_with_indexation != null && result?.tax_without_indexation != null && (
                      <div style={{ fontSize: 13, color: "#375623", fontWeight: 700, textAlign: "center" }}>
                        Choosing {result.recommended_option === "with_indexation" ? "Option A" : "Option B"} saves{" "}
                        {fmtInr(Math.abs(result.tax_with_indexation - result.tax_without_indexation))} in tax
                      </div>
                    )}
                  </div>
                  <div>
                    <label style={labelStyle}>Select your preferred option</label>
                    {[
                      { val: "auto", label: "Auto (use recommended option)" },
                      { val: "with_indexation_20", label: "Option A: 20% with indexation" },
                      { val: "without_indexation_12_5", label: "Option B: 12.5% without indexation" },
                    ].map(o => (
                      <label key={o.val} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, cursor: "pointer", fontSize: 13 }}>
                        <input type="radio" name="taxOption" value={o.val} checked={taxOption === o.val} onChange={e => setTaxOption(e.target.value)} />
                        {o.label}
                      </label>
                    ))}
                  </div>
                </>
              ) : (
                <div style={{ background: "#EBF3FB", border: "1px solid #2E75B6", borderRadius: 8, padding: "14px 16px", fontSize: 13, color: "#1F4E79" }}>
                  <strong>Only Option B (12.5% without indexation)</strong> is available for properties purchased on or after 23 July 2024, as per Finance Act 2024.
                  {result?.ltcg_without_indexation != null && (
                    <div style={{ marginTop: 8, color: "#333" }}>
                      Capital Gain: {fmtInr(result.ltcg_without_indexation)} | Tax @ 12.5%: {fmtInr(result.tax_without_indexation)}
                    </div>
                  )}
                </div>
              )}

              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 16 }}>
                <button onClick={() => setActiveTab(1)} style={{ padding: "8px 20px", background: "transparent", color: "#1F4E79", border: "1.5px solid #1F4E79", borderRadius: 8, fontWeight: 600, cursor: "pointer" }}>
                  ← Back
                </button>
                <button onClick={() => setActiveTab(3)} style={{ padding: "8px 20px", background: "#1F4E79", color: "#fff", border: "none", borderRadius: 8, fontWeight: 600, cursor: "pointer" }}>
                  Next: Exemptions →
                </button>
              </div>
            </div>
          )}

          {/* ── TAB 3: Exemptions (only for LTCG) ────────────────────────── */}
          {activeTab === 3 && isLongTerm && (
            <div style={cardStyle}>
              <div style={sectionHeadStyle}>Exemptions (Sections 82–86)</div>
              <div style={{ fontSize: 12, color: "var(--text-muted, #888)", marginBottom: 16 }}>
                Select the exemptions you are claiming. Only relevant sections are shown based on your property type.
              </div>

              {/* Section 82 — Residential */}
              {propertyType === "residential" && (
                <ExemptionBlock
                  sectionNum="82"
                  oldNum="54"
                  title="Profit on sale of residential house"
                  checked={s82claim}
                  onToggle={() => setS82claim(v => !v)}
                  onInfo={() => setInfoModal("82")}
                >
                  {s82claim && (
                    <div>
                      <div style={{ marginBottom: 10 }}>
                        <label style={labelStyle}>Investment Status</label>
                        <select style={inputStyle} value={s82type} onChange={e => setS82type(e.target.value)}>
                          <option value="purchased_before_sale">Purchased new house before sale (within 1 year)</option>
                          <option value="purchased_after_sale">Purchased new house after sale (within 2 years)</option>
                          <option value="under_construction">Under construction (within 3 years)</option>
                          <option value="cgas_deposit">Deposited in Capital Gain Account Scheme (CGAS)</option>
                        </select>
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
                        <div>
                          <label style={labelStyle}>Cost of New House (₹)</label>
                          <input style={inputStyle} type="number" value={s82cost} onChange={e => setS82cost(e.target.value)} placeholder="0" />
                        </div>
                        <div>
                          <label style={labelStyle}>Date of Purchase / Completion</label>
                          <input style={inputStyle} value={s82date} onChange={e => setS82date(e.target.value)} placeholder="DD/MM/YYYY" />
                        </div>
                      </div>
                      <div style={{ marginBottom: 10 }}>
                        <label style={labelStyle}>Is new house in India?</label>
                        <div style={{ display: "flex", gap: 16 }}>
                          {[["Yes", true], ["No", false]].map(([l, v]) => (
                            <label key={String(l)} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer" }}>
                              <input type="radio" checked={s82india === v} onChange={() => setS82india(v as boolean)} /> {String(l)}
                            </label>
                          ))}
                        </div>
                      </div>
                      {s82type === "cgas_deposit" && (
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
                          <div>
                            <label style={labelStyle}>CGAS Amount (₹)</label>
                            <input style={inputStyle} type="number" value={s82cgas} onChange={e => setS82cgas(e.target.value)} placeholder="0" />
                          </div>
                          <div>
                            <label style={labelStyle}>Bank Name</label>
                            <input style={inputStyle} value={s82cgasBank} onChange={e => setS82cgasBank(e.target.value)} placeholder="e.g. SBI" />
                          </div>
                        </div>
                      )}
                      {result && (() => {
                        const ex = result.exemption_details?.find(e => e.section_new === "Section 82");
                        if (!ex) return null;
                        return (
                          <div style={{ background: "#E2EFDA", border: "1px solid #A9C9A0", borderRadius: 6, padding: "8px 12px", fontSize: 12, color: "#375623" }}>
                            Exemption available: <strong>{fmtInr(ex.exemption_amount)}</strong>
                            {(parseFloat(s82cost) || 0) > 10_00_00_000 && (
                              <div style={{ color: "#7F3F00", marginTop: 4 }}>Note: Exemption capped at ₹10 crore (Sec 82(7))</div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </ExemptionBlock>
              )}

              {/* Section 83 — Agricultural */}
              {(propertyType === "agricultural_rural" || propertyType === "agricultural_urban") && (
                <ExemptionBlock
                  sectionNum="83" oldNum="54B"
                  title="Capital gains on agricultural land transfer"
                  checked={s83claim} onToggle={() => setS83claim(v => !v)} onInfo={() => setInfoModal("83")}
                >
                  {s83claim && (
                    <div>
                      <div style={{ marginBottom: 10 }}>
                        <label style={labelStyle}>Was land used for agriculture for last 2 years?</label>
                        <div style={{ display: "flex", gap: 16 }}>
                          {[["Yes", true], ["No", false]].map(([l, v]) => (
                            <label key={String(l)} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer" }}>
                              <input type="radio" checked={s83usedAg === v} onChange={() => setS83usedAg(v as boolean)} /> {String(l)}
                            </label>
                          ))}
                        </div>
                        {!s83usedAg && <div style={{ color: "#9C0006", fontSize: 11, marginTop: 4 }}>⚠ Agricultural use for 2 years is mandatory for this exemption.</div>}
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
                        <div>
                          <label style={labelStyle}>New Agricultural Land Cost (₹)</label>
                          <input style={inputStyle} type="number" value={s83landCost} onChange={e => setS83landCost(e.target.value)} placeholder="0" />
                        </div>
                        <div>
                          <label style={labelStyle}>CGAS Amount (if not yet purchased) (₹)</label>
                          <input style={inputStyle} type="number" value={s83cgas} onChange={e => setS83cgas(e.target.value)} placeholder="0" />
                        </div>
                      </div>
                      {result && (() => {
                        const ex = result.exemption_details?.find(e => e.section_new === "Section 83");
                        if (!ex) return null;
                        return <div style={{ background: "#E2EFDA", border: "1px solid #A9C9A0", borderRadius: 6, padding: "8px 12px", fontSize: 12, color: "#375623" }}>Exemption: <strong>{fmtInr(ex.exemption_amount)}</strong></div>;
                      })()}
                    </div>
                  )}
                </ExemptionBlock>
              )}

              {/* Section 84 — Compulsory Acquisition */}
              {natureOfSale === "compulsory_acquisition" && (
                <ExemptionBlock
                  sectionNum="84" oldNum="54D"
                  title="Compulsory acquisition of industrial property"
                  checked={s84claim} onToggle={() => setS84claim(v => !v)} onInfo={() => setInfoModal("84")}
                >
                  {s84claim && (
                    <div>
                      <div style={{ marginBottom: 10 }}>
                        <label style={labelStyle}>Was property used for industrial purposes for 2 years?</label>
                        <div style={{ display: "flex", gap: 16 }}>
                          {[["Yes", true], ["No", false]].map(([l, v]) => (
                            <label key={String(l)} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer" }}>
                              <input type="radio" checked={s84industrial === v} onChange={() => setS84industrial(v as boolean)} /> {String(l)}
                            </label>
                          ))}
                        </div>
                      </div>
                      <div>
                        <label style={labelStyle}>New Industrial Property Investment (₹)</label>
                        <input style={inputStyle} type="number" value={s84investment} onChange={e => setS84investment(e.target.value)} placeholder="0" />
                      </div>
                    </div>
                  )}
                </ExemptionBlock>
              )}

              {/* Section 85 — Bonds (available for land/building LTCG) */}
              {(propertyType !== "agricultural_rural") && (
                <ExemptionBlock
                  sectionNum="85" oldNum="54EC"
                  title="Investment in NHAI / REC Bonds"
                  checked={s85claim} onToggle={() => setS85claim(v => !v)} onInfo={() => setInfoModal("85")}
                >
                  {s85claim && (
                    <div>
                      <div style={{ marginBottom: 10 }}>
                        <label style={labelStyle}>Bond Name</label>
                        <select style={inputStyle} value={s85bond} onChange={e => setS85bond(e.target.value)}>
                          <option value="NHAI">NHAI (National Highways Authority of India)</option>
                          <option value="REC">REC (Rural Electrification Corporation)</option>
                        </select>
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
                        <div>
                          <label style={labelStyle}>Amount Invested (₹)</label>
                          <input style={inputStyle} type="number" value={s85amount} onChange={e => setS85amount(e.target.value)} placeholder="0 (max Rs 50 lakhs)" />
                        </div>
                        <div>
                          <label style={labelStyle}>Date of Investment</label>
                          <input style={inputStyle} value={s85date} onChange={e => setS85date(e.target.value)} placeholder="DD/MM/YYYY" />
                        </div>
                      </div>
                      {parseFloat(s85amount) > 50_00_000 && (
                        <div style={{ background: "#FFF2CC", border: "1px solid #C9A84C", borderRadius: 6, padding: "6px 12px", fontSize: 12, color: "#5C3D00", marginBottom: 8 }}>
                          ⚠ Maximum Rs 50,00,000 eligible. Excess of ₹ {(parseFloat(s85amount) - 50_00_000).toLocaleString("en-IN")} not eligible.
                        </div>
                      )}
                      {s85date && dateOfSale && (() => {
                        try {
                          const [id, im, iy] = s85date.split("/").map(Number);
                          const [sd, sm, sy] = dateOfSale.split("/").map(Number);
                          const inv = new Date(iy, im - 1, id);
                          const sal = new Date(sy, sm - 1, sd);
                          const diff = (inv.getTime() - sal.getTime()) / 86400000;
                          if (diff > 182) return <div style={{ background: "#FCE4D6", border: "1px solid #f99", borderRadius: 6, padding: "6px 12px", fontSize: 12, color: "#9C0006", marginBottom: 8 }}>⚠ Investment must be within 6 months of sale date. This investment is {Math.round(diff)} days after sale.</div>;
                        } catch { }
                        return null;
                      })()}
                      {result && (() => {
                        const ex = result.exemption_details?.find(e => e.section_new === "Section 85");
                        if (!ex) return null;
                        return <div style={{ background: "#E2EFDA", border: "1px solid #A9C9A0", borderRadius: 6, padding: "8px 12px", fontSize: 12, color: "#375623" }}>Exemption: <strong>{fmtInr(ex.exemption_amount)}</strong></div>;
                      })()}
                    </div>
                  )}
                </ExemptionBlock>
              )}

              {/* Section 86 — Any LT Asset (not residential) */}
              {propertyType !== "residential" && (
                <ExemptionBlock
                  sectionNum="86" oldNum="54F"
                  title="Any LT asset (not residential house) — buy residential house"
                  checked={s86claim} onToggle={() => setS86claim(v => !v)} onInfo={() => setInfoModal("86")}
                >
                  {s86claim && (
                    <div>
                      <div style={{ marginBottom: 10 }}>
                        <label style={labelStyle}>Residential houses owned on date of sale (excluding new house)</label>
                        <select style={inputStyle} value={s86houses} onChange={e => setS86houses(e.target.value)}>
                          <option value="0">0 — No other house</option>
                          <option value="1">1 — One other house</option>
                          <option value="2">2 or more — NOT eligible</option>
                        </select>
                        {parseInt(s86houses) >= 2 && (
                          <div style={{ background: "#FCE4D6", border: "1px solid #f99", borderRadius: 6, padding: "8px 12px", fontSize: 12, color: "#9C0006", marginTop: 6 }}>
                            Section 86 (formerly 54F) is NOT available if you own more than 1 residential house on date of sale.
                          </div>
                        )}
                      </div>
                      {parseInt(s86houses) < 2 && (
                        <>
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
                            <div>
                              <label style={labelStyle}>Cost of New Residential House (₹)</label>
                              <input style={inputStyle} type="number" value={s86cost} onChange={e => setS86cost(e.target.value)} placeholder="0" />
                            </div>
                            <div>
                              <label style={labelStyle}>Date of Purchase</label>
                              <input style={inputStyle} value={s86date} onChange={e => setS86date(e.target.value)} placeholder="DD/MM/YYYY" />
                            </div>
                          </div>
                          <div style={{ marginBottom: 10 }}>
                            <label style={labelStyle}>Is new house in India?</label>
                            <div style={{ display: "flex", gap: 16 }}>
                              {[["Yes", true], ["No", false]].map(([l, v]) => (
                                <label key={String(l)} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer" }}>
                                  <input type="radio" checked={s86india === v} onChange={() => setS86india(v as boolean)} /> {String(l)}
                                </label>
                              ))}
                            </div>
                          </div>
                          <div style={{ marginBottom: 10 }}>
                            <label style={labelStyle}>CGAS Amount (₹) <span style={{ fontWeight: 400, color: "#888", fontSize: 11 }}>(if investment not yet made)</span></label>
                            <input style={inputStyle} type="number" value={s86cgas} onChange={e => setS86cgas(e.target.value)} placeholder="0" />
                          </div>
                          {result && s86cost && (
                            <div style={{ background: "#EBF3FB", border: "1px solid #2E75B6", borderRadius: 6, padding: "8px 12px", fontSize: 12, color: "#1F4E79", marginBottom: 6 }}>
                              Proportionate Exemption = LTCG × New House Cost ÷ Net Consideration
                              {result.exemption_details?.find(e => e.section_new === "Section 86") && (
                                <div style={{ fontWeight: 700, marginTop: 4 }}>
                                  = {fmtInr(result.exemption_details?.find(e => e.section_new === "Section 86")?.exemption_amount)}
                                </div>
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </ExemptionBlock>
              )}

              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 16 }}>
                <button onClick={() => setActiveTab(2)} style={{ padding: "8px 20px", background: "transparent", color: "#1F4E79", border: "1.5px solid #1F4E79", borderRadius: 8, fontWeight: 600, cursor: "pointer" }}>
                  ← Back
                </button>
                <button onClick={() => setActiveTab(4)} style={{ padding: "8px 20px", background: "#1F4E79", color: "#fff", border: "none", borderRadius: 8, fontWeight: 600, cursor: "pointer" }}>
                  Next: Review →
                </button>
              </div>
            </div>
          )}

          {/* ── TAB 4 / Last tab: Review and Download ─────────────────────── */}
          {((isLongTerm && activeTab === 4) || (!isLongTerm && activeTab === 2)) && (
            <div style={cardStyle}>
              <div style={sectionHeadStyle}>Review and Download</div>

              {result?.warnings && result.warnings.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  {result.warnings.map((w, i) => (
                    <div key={i} style={{ background: "#FFF2CC", border: "1px solid #C9A84C", borderRadius: 8, padding: "8px 14px", fontSize: 12, color: "#5C3D00", marginBottom: 8 }}>
                      ⚠ {w}
                    </div>
                  ))}
                </div>
              )}

              <div style={{ background: "#EBF3FB", border: "1px solid #2E75B6", borderRadius: 10, padding: "16px 20px", marginBottom: 16, fontSize: 12 }}>
                <div style={{ fontWeight: 700, color: "#1F4E79", marginBottom: 10, fontSize: 13 }}>Report will include 5 sheets:</div>
                {["Property Details & Capital Gain Calculation", "Exemption Details (Sections 82–86)", "Tax Calculation (Both Options)", "Capital Gain Account Scheme (CGAS) Tracker", "How to Report in ITR (Utility, Computax, Winman)"].map((s, i) => (
                  <div key={i} style={{ display: "flex", gap: 8, marginBottom: 4, color: "#333" }}>
                    <span style={{ color: "#2E75B6", fontWeight: 700 }}>{i + 1}.</span> {s}
                  </div>
                ))}
              </div>

              <div style={{ marginBottom: 16, padding: "12px 16px", background: "#f8f9fa", borderRadius: 8, border: "1px solid #e5e7eb", fontSize: 12, color: "#555" }}>
                <strong>Act Reference:</strong> Income Tax Act, 2025 (Sections 82–86) applies for sales on or after 01-04-2026.
                For sales before that date, Income Tax Act, 1961 (Sections 54–54F) applies.
                Both section references are included in the report.
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 8 }}>
                <button
                  onClick={handleDownloadJpeg}
                  disabled={downloadingJpeg || !result?.success}
                  style={{
                    padding: "10px 8px", background: downloadingJpeg ? "#ccc" : "#375623",
                    color: "#fff", border: "none", borderRadius: 8,
                    fontWeight: 600, fontSize: 13, cursor: downloadingJpeg ? "not-allowed" : "pointer",
                  }}
                >
                  {downloadingJpeg ? "..." : "Save as JPEG"}
                </button>
                <button
                  onClick={handleDownloadPdf}
                  disabled={downloadingPdf || !result?.success}
                  style={{
                    padding: "10px 8px", background: downloadingPdf ? "#ccc" : "#C00000",
                    color: "#fff", border: "none", borderRadius: 8,
                    fontWeight: 600, fontSize: 13, cursor: downloadingPdf ? "not-allowed" : "pointer",
                  }}
                >
                  {downloadingPdf ? "..." : "Save as PDF"}
                </button>
                <button
                  onClick={handleDownload}
                  disabled={downloading || !saleConsideration || !dateOfPurchase || !dateOfSale}
                  style={{
                    padding: "10px 8px", background: downloading ? "#ccc" : "#1F4E79",
                    color: "#fff", border: "none", borderRadius: 8,
                    fontWeight: 600, fontSize: 13, cursor: downloading ? "not-allowed" : "pointer",
                  }}
                >
                  {downloading ? "Generating..." : "Download Excel"}
                </button>
              </div>

              <RabbitProgress loading={downloading} estimatedSeconds={10} />

              <div style={{ display: "flex", justifyContent: "flex-start", marginTop: 16 }}>
                <button onClick={() => setActiveTab(isLongTerm ? 3 : 1)} style={{ padding: "8px 20px", background: "transparent", color: "#1F4E79", border: "1.5px solid #1F4E79", borderRadius: 8, fontWeight: 600, cursor: "pointer" }}>
                  ← Back
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ── RIGHT: Live Results Panel (40%) ─────────────────────────────── */}
        <div ref={resultPanelRef} style={{ flex: "0 0 38%", minWidth: 0, position: "sticky", top: 80 }}>
          <AnimatePresence mode="wait">
            <motion.div
              key={result ? "has-result" : "no-result"}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              {!result || !result.success ? (
                <div style={{ background: "var(--card-bg, #fff)", borderRadius: 12, border: "1.5px solid var(--border, #e5e7eb)", padding: "24px 20px", textAlign: "center" }}>
                  <div style={{ fontSize: 40, marginBottom: 12 }}>🏠</div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary, #333)", marginBottom: 8 }}>Capital Gain Summary</div>
                  <div style={{ fontSize: 12, color: "var(--text-muted, #888)" }}>
                    Fill in property details to see a live calculation preview here.
                  </div>
                </div>
              ) : (
                <div style={{ background: "var(--card-bg, #fff)", borderRadius: 12, border: "1.5px solid var(--border, #e5e7eb)", overflow: "hidden" }}>
                  {/* Header */}
                  <div style={{ background: "#1F4E79", padding: "14px 20px" }}>
                    <div style={{ fontSize: 14, fontWeight: 800, color: "#fff" }}>
                      {clientName || "Capital Gain Summary"}
                    </div>
                    {clientName && (
                      <div style={{ fontSize: 12, color: "rgba(255,255,255,0.9)", marginTop: 1 }}>
                        Capital Gain Summary
                      </div>
                    )}
                    <div style={{ fontSize: 11, color: "rgba(255,255,255,0.75)", marginTop: 2 }}>
                      {financialYear && `FY ${financialYear} · `}
                      {result.gain_type} Capital Gain
                      {result.holding_years != null && ` · ${result.holding_years}y held`}
                    </div>
                  </div>

                  <div style={{ padding: "16px 20px" }}>
                    {/* Gain type badge */}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                      <span style={{ fontSize: 12, color: "var(--text-muted, #666)" }}>Type</span>
                      <span style={{
                        padding: "3px 12px", borderRadius: 20, fontSize: 12, fontWeight: 700,
                        background: result.is_long_term ? "#E2EFDA" : "#FFF2CC",
                        color: result.is_long_term ? "#375623" : "#7F6000",
                      }}>
                        {result.gain_type}
                      </span>
                    </div>

                    <SummaryRow label="Sale Consideration" value={fmtInr(result.effective_sale_value)} />
                    {result.section_50c_applies && (
                      <div style={{ fontSize: 11, color: "#7F6000", marginTop: -6, marginBottom: 8, paddingLeft: 4 }}>⚠ Section 50C applied</div>
                    )}
                    <SummaryRow label="Less: Expenses on Sale" value={fmtInrSigned(-(parseFloat(expensesOnSale) || 0) * (parseFloat(ownershipShare) / 100))} dim />
                    <SummaryRow label="Net Sale Consideration" value={fmtInr(result.net_sale_consideration)} bold />

                    <div style={{ borderTop: "1px solid var(--border, #eee)", margin: "10px 0" }} />

                    {result.is_long_term ? (
                      <>
                        {result.both_options_available ? (
                          <>
                            <SummaryRow label="LTCG (With Indexation)" value={fmtInr(result.ltcg_with_indexation)} />
                            <SummaryRow label="LTCG (Without Indexation)" value={fmtInr(result.ltcg_without_indexation)} />
                            <SummaryRow label={`LTCG (${result.chosen_option === "with_indexation" ? "Chosen: Option A" : "Chosen: Option B"})`} value={fmtInr(result.ltcg)} bold />
                          </>
                        ) : (
                          <SummaryRow label="Long Term Capital Gain" value={fmtInr(result.ltcg)} bold />
                        )}

                        <div style={{ borderTop: "1px solid var(--border, #eee)", margin: "10px 0" }} />
                        <SummaryRow label="Less: Exemptions" value={fmtInrSigned(-(result.total_exemption || 0))} dim />
                        <SummaryRow label="Taxable LTCG" value={fmtInr(result.taxable_ltcg)} bold />

                        <div style={{ borderTop: "1px solid var(--border, #eee)", margin: "10px 0" }} />
                        <SummaryRow label={`Tax @ ${typeof result.tax_rate === "number" ? (result.tax_rate * 100).toFixed(1) + "%" : result.tax_rate}`} value={fmtInr(result.tax_on_gain)} />
                        <SummaryRow label="Add: Cess @ 4%" value={fmtInr(result.cess)} />
                        <SummaryRow label="Total Tax Payable" value={fmtInr(result.total_tax_payable)} bold highlight />
                      </>
                    ) : (
                      <>
                        <SummaryRow label="Short Term Capital Gain" value={fmtInr(result.stcg)} bold />
                        <div style={{ background: "#FFF2CC", borderRadius: 6, padding: "8px 12px", fontSize: 11, color: "#5C3D00", marginTop: 8 }}>
                          STCG taxable at slab rate. Add to total income.
                        </div>
                      </>
                    )}

                    {/* CII info */}
                    {result.is_long_term && (
                      <div style={{ borderTop: "1px solid var(--border, #eee)", marginTop: 12, paddingTop: 10 }}>
                        <div style={{ fontSize: 11, color: "var(--text-muted, #888)", marginBottom: 4 }}>CII Details</div>
                        <div style={{ fontSize: 11, color: "var(--text-primary, #555)" }}>
                          Purchase FY {result.cii_purchase_fy}: {result.cii_purchase} → Sale FY {result.sale_fy}: {result.cii_sale}
                        </div>
                      </div>
                    )}

                    {/* Exemptions summary */}
                    {result.exemption_details && result.exemption_details.length > 0 && (
                      <div style={{ borderTop: "1px solid var(--border, #eee)", marginTop: 12, paddingTop: 10 }}>
                        <div style={{ fontSize: 11, color: "var(--text-muted, #888)", marginBottom: 6 }}>Exemptions Claimed</div>
                        {result.exemption_details.map((ex, i) => (
                          <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 11, marginBottom: 3 }}>
                            <span style={{ color: "var(--text-primary, #555)" }}>{ex.section_new} ({ex.section_old})</span>
                            <span style={{ fontWeight: 600, color: "#375623" }}>{fmtInr(ex.exemption_amount)}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Surcharge table */}
                    {result.is_long_term && <SurchargeTable />}
                  </div>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Info Modal */}
      {infoModal && <InfoModal section={infoModal} onClose={() => setInfoModal(null)} />}
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function ExemptionBlock({
  sectionNum, oldNum, title, checked, onToggle, onInfo, children
}: {
  sectionNum: string; oldNum: string; title: string;
  checked: boolean; onToggle: () => void; onInfo: () => void;
  children?: React.ReactNode;
}) {
  return (
    <div style={{ border: `1.5px solid ${checked ? "#2E75B6" : "var(--border, #ddd)"}`, borderRadius: 10, marginBottom: 12, overflow: "hidden" }}>
      <div style={{ background: checked ? "#EBF3FB" : "var(--offwhite, #f8f9fa)", padding: "12px 16px", display: "flex", alignItems: "center", gap: 10 }}>
        <input type="checkbox" checked={checked} onChange={onToggle} style={{ width: 16, height: 16, cursor: "pointer" }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text-primary, #111)" }}>
            Section {sectionNum} <span style={{ fontWeight: 400, color: "#888", fontSize: 11 }}>(formerly Section {oldNum}, IT Act 1961)</span>
          </div>
          <div style={{ fontSize: 12, color: "var(--text-muted, #666)" }}>{title}</div>
        </div>
        <button
          onClick={onInfo}
          title="View legal text"
          style={{ background: "none", border: "1px solid #2E75B6", borderRadius: "50%", width: 24, height: 24, cursor: "pointer", color: "#2E75B6", fontSize: 12, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
        >
          ℹ
        </button>
      </div>
      {checked && children && (
        <div style={{ padding: "14px 16px", borderTop: "1px solid var(--border, #e5e7eb)" }}>
          {children}
        </div>
      )}
    </div>
  );
}

function SummaryRow({ label, value, bold, dim, highlight }: {
  label: string; value: string; bold?: boolean; dim?: boolean; highlight?: boolean;
}) {
  return (
    <div style={{
      display: "flex", justifyContent: "space-between", alignItems: "center",
      marginBottom: 6, padding: highlight ? "6px 8px" : "2px 0",
      background: highlight ? "#E2EFDA" : "transparent",
      borderRadius: highlight ? 6 : 0,
    }}>
      <span style={{ fontSize: 12, color: dim ? "var(--text-muted, #888)" : "var(--text-primary, #555)", fontWeight: bold ? 700 : 400 }}>{label}</span>
      <span style={{ fontSize: 12, fontWeight: bold || highlight ? 700 : 500, color: highlight ? "#375623" : "var(--text-primary, #111)" }}>{value}</span>
    </div>
  );
}
